import React, { useState, useEffect } from 'react';
import {
  Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Typography, Chip, TextField, InputAdornment, CircularProgress, Card, CardContent,
  Stack, Tooltip, TablePagination, Dialog, DialogTitle, DialogContent, DialogActions,
  Switch, FormControlLabel, Alert, Avatar, Fade, Zoom,
  Autocomplete, MenuItem
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon,
  Refresh as RefreshIcon, People as PeopleIcon, AdminPanelSettings as AdminIcon,
  Security as SecurityIcon, CalendarMonth as CalendarIcon, CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon, SupervisorAccount as SupervisorIcon, Block as BlockIcon,
  Verified as VerifiedIcon, Close as CloseIcon, Save as SaveIcon, Store as StoreIcon,
  Badge as BadgeIcon, LockOpen as UnlockIcon, Lock as LockIcon,
  MonitorHeart as SessionIcon, Circle as CircleIcon, Language as GlobeIcon,
  Wifi as WifiIcon, History as HistoryIcon,
  NotificationsActive as MonitorOnIcon, NotificationsOff as MonitorOffIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAppSelector } from '../../hooks/redux.hooks';
import axiosInstance from '../../services/axiosInstance';
import { useConfirm } from '../../hooks/useConfirm';

const translations = {
  en: {
    title: 'Users Management', subtitle: 'Manage system users and their access',
    addUser: 'Add User', userName: 'Username', fullName: 'Full Name',
    email: 'Email', phone: 'Phone', roles: 'Roles', status: 'Status', lastLogin: 'Last Login',
    actions: 'Actions', search: 'Search users...', loading: 'Loading...', noData: 'No users found',
    confirmDelete: 'Are you sure you want to delete this user?', active: 'Active', inactive: 'Inactive',
    totalUsers: 'Total Users', activeUsers: 'Active Users', adminUsers: 'Administrators', blockedUsers: 'Locked',
    refresh: 'Refresh', edit: 'Edit', delete: 'Delete', permissions: 'Permissions', fiscalYears: 'Fiscal Years',
    unlock: 'Unlock Account', sessionMonitor: 'Session Monitor', ipAddress: 'IP Address', country: 'Country',
    sessionStatus: 'Session', lastLogout: 'Last Logout', lastActivity: 'Last Activity',
    online: 'Online', idle: 'Sleep', offline: 'Offline', locked: 'Locked',
    save: 'Save', cancel: 'Cancel', password: 'Password', confirmPassword: 'Confirm Password', rowsPerPage: 'Rows:',
    userDetails: 'User Details', selectRoles: 'Select Roles', fullNameAr: 'Full Name (Arabic)',
    superAdmin: 'Super Admin', admin: 'Admin', user: 'User', createUser: 'Create New User', editUser: 'Edit User',
    resetPassword: 'Reset Password', newPassword: 'New Password', leaveEmptyToKeep: 'Leave empty to keep current password',
    mainBranch: 'Main Branch', allowedBranches: 'Allowed Branches', selectBranch: 'Select Branch',
    selectAllowedBranches: 'Select Allowed Branches', allBranches: 'All Branches', branchAccess: 'Branch Access',
    linkedEmployee: 'Linked Employee', selectEmployee: 'Select Employee (Custody Holder)',
    noEmployee: 'No linked employee',
    loginHistory: 'Login History', last5Logins: 'Last 5 Logins',
    loginDate: 'Login', logoutDate: 'Logout', sessionDuration: 'Duration',
    userAgent: 'Device', noHistory: 'No login history yet',
    monitorLogin: 'Monitor Login', monitoringOn: 'Monitoring ON', monitoringOff: 'Monitoring OFF',
    monitorTooltip: 'Click to receive a notification each time this user logs in',
    actions2: 'Actions',
  },
  ar: {
    title: 'إدارة المستخدمين', subtitle: 'إدارة مستخدمي النظام وصلاحياتهم',
    addUser: 'إضافة مستخدم', userName: 'اسم المستخدم', fullName: 'الاسم الكامل',
    email: 'البريد الإلكتروني', phone: 'الهاتف', roles: 'الأدوار', status: 'الحالة', lastLogin: 'آخر دخول',
    actions: 'الإجراءات', search: 'البحث عن مستخدم...', loading: 'جاري التحميل...', noData: 'لا يوجد مستخدمين',
    confirmDelete: 'هل أنت متأكد من حذف هذا المستخدم؟', active: 'نشط', inactive: 'غير نشط',
    totalUsers: 'إجمالي المستخدمين', activeUsers: 'المستخدمين النشطين', adminUsers: 'المسؤولين', blockedUsers: 'محظور',
    refresh: 'تحديث', edit: 'تعديل', delete: 'حذف', permissions: 'الصلاحيات', fiscalYears: 'السنوات المالية',
    unlock: 'فتح الحساب', sessionMonitor: 'مراقبة الجلسات', ipAddress: 'عنوان IP', country: 'الدولة',
    sessionStatus: 'الجلسة', lastLogout: 'آخر خروج', lastActivity: 'آخر نشاط',
    online: 'متصل', idle: 'نائم', offline: 'غير متصل', locked: 'مقفل',
    save: 'حفظ', cancel: 'إلغاء', password: 'كلمة المرور', confirmPassword: 'تأكيد كلمة المرور', rowsPerPage: 'الصفوف:',
    userDetails: 'بيانات المستخدم', selectRoles: 'اختر الأدوار', fullNameAr: 'الاسم بالعربي',
    superAdmin: 'مدير النظام', admin: 'مسؤول', user: 'مستخدم', createUser: 'إنشاء مستخدم جديد', editUser: 'تعديل المستخدم',
    resetPassword: 'إعادة تعيين كلمة المرور', newPassword: 'كلمة المرور الجديدة', leaveEmptyToKeep: 'اتركه فارغاً للإبقاء على كلمة المرور الحالية',
    mainBranch: 'الفرع الرئيسي', allowedBranches: 'الفروع المسموحة', selectBranch: 'اختر الفرع',
    selectAllowedBranches: 'اختر الفروع المسموحة', allBranches: 'جميع الفروع', branchAccess: 'صلاحيات الفروع',
    linkedEmployee: 'الموظف المرتبط', selectEmployee: 'اختر الموظف (حامل العهدة)',
    noEmployee: 'لا يوجد موظف مرتبط',
    loginHistory: 'سجل الدخول', last5Logins: 'آخر 5 تسجيلات دخول',
    loginDate: 'الدخول', logoutDate: 'الخروج', sessionDuration: 'المدة',
    userAgent: 'الجهاز', noHistory: 'لا يوجد سجل دخول بعد',
    monitorLogin: 'مراقبة الدخول', monitoringOn: 'مفعّلة', monitoringOff: 'متوقفة',
    monitorTooltip: 'اضغط لتلقي إشعار في كل مرة يسجل فيها هذا المستخدم الدخول',
    actions2: 'الإجراءات',
  },
};

interface User {
  userId: number; userName: string; fullName: string; fullNameAr?: string;
  email?: string; phone?: string; isActive: boolean; lastLoginDate?: string;
  lastLogoutDate?: string; lastActivityDate?: string;
  isOnline: boolean; failedLoginAttempts: number; lockoutEnd?: string; isLocked: boolean;
  sessionStatus: 'Active' | 'Sleep' | 'Offline';
  lastLoginIp?: string; lastLoginCountry?: string;
  branchId?: number; allowedBranchIds?: number[]; employeeId?: number;
  userRoles?: { role: { roleId: number; roleName: string; roleNameAr: string } }[];
}

interface Role { roleId: number; roleName: string; roleNameAr: string; isSystemRole: boolean; }
interface Branch { branchId: number; branchCode: string; branchName: string; branchNameAr: string; }
interface Employee { employeeId: number; fullName: string; fullNameAr: string; employeeCode: string; }

const UsersPage: React.FC = () => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const isDark = mode === 'dark';
  const isSuperAdmin = useAppSelector(s => s.auth.user?.isSuperAdmin ?? false);
  const confirm = useConfirm();

  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterBranch, setFilterBranch] = useState<string>('');
  const [filterSession, setFilterSession] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyUser, setHistoryUser] = useState<{ userId: number; userName: string; fullName: string } | null>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [monitorBusyId, setMonitorBusyId] = useState<number | null>(null);

  const [formData, setFormData] = useState({
    userName: '', fullName: '', fullNameAr: '', email: '', phone: '',
    password: '', confirmPassword: '', newPassword: '', isActive: true, 
    roleIds: [] as number[], branchId: null as number | null, 
    allowedBranchIds: [] as number[], employeeId: null as number | null
  });

  useEffect(() => { loadUsers(); loadRoles(); loadBranches(); loadEmployees(); }, []);
  useEffect(() => { applyFilters(); }, [searchTerm, filterStatus, filterRole, filterBranch, filterSession, users]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get('/Users');
      setUsers(data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const loadRoles = async () => {
    try {
      const { data } = await axiosInstance.get('/Roles');
      setRoles(data);
    } catch (err) { console.error(err); }
  };

  const loadBranches = async () => {
    try {
      const { data } = await axiosInstance.get('/Branches');
      setBranches(data);
    } catch (err) { console.error(err); }
  };

  const loadEmployees = async () => {
    try {
      const { data } = await axiosInstance.get('/Employees');
      setEmployees(data);
    } catch (err) { console.error(err); }
  };

  const applyFilters = () => {
    let filtered = users;

    if (filterStatus === 'active') filtered = filtered.filter(u => u.isActive);
    else if (filterStatus === 'inactive') filtered = filtered.filter(u => !u.isActive);
    else if (filterStatus === 'locked') filtered = filtered.filter(u => u.isLocked);

    if (filterRole) filtered = filtered.filter(u => u.userRoles?.some(r => r.role.roleName === filterRole));

    if (filterBranch) filtered = filtered.filter(u => {
      const b = branches.find(br => br.branchName === filterBranch);
      return b && (u.branchId === b.branchId || u.allowedBranchIds?.includes(b.branchId));
    });

    if (filterSession === 'online') filtered = filtered.filter(u => u.isOnline);
    else if (filterSession === 'offline') filtered = filtered.filter(u => !u.isOnline);

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u =>
        u.userName.toLowerCase().includes(term) ||
        u.fullName.toLowerCase().includes(term) ||
        u.email?.toLowerCase().includes(term)
      );
    }

    setFilteredUsers(filtered);
    setPage(0);
  };

  const handleAdd = () => {
    setSelectedUser(null);
    setFormData({ userName: '', fullName: '', fullNameAr: '', email: '', phone: '', password: '', confirmPassword: '', newPassword: '', isActive: true, roleIds: [], branchId: null, allowedBranchIds: [], employeeId: null });
    setError('');
    setDialogOpen(true);
  };

  const handleEdit = (user: User) => {
    setSelectedUser(user);
    setFormData({
      userName: user.userName, fullName: user.fullName, fullNameAr: user.fullNameAr || '',
      email: user.email || '', phone: user.phone || '', password: '', confirmPassword: '', newPassword: '',
      isActive: user.isActive, roleIds: user.userRoles?.map(ur => ur.role.roleId) || [],
      branchId: user.branchId || null, allowedBranchIds: user.allowedBranchIds || [],
      employeeId: user.employeeId || null
    });
    setError('');
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    const ok = await confirm({ message: t.confirmDelete, severity: 'error' });
    if (!ok) return;
    try { await axiosInstance.delete(`/Users/${id}`); loadUsers(); }
    catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    if (!selectedUser && formData.password !== formData.confirmPassword) {
      setError(isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match'); return;
    }
    setSaving(true);
    try {
      const payload = { ...formData, employeeId: formData.employeeId || null };
      if (selectedUser) {
        await axiosInstance.put(`/Users/${selectedUser.userId}`, { ...payload, userId: selectedUser.userId });
      } else {
        await axiosInstance.post('/Users', payload);
      }
      setDialogOpen(false);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.message || (isRTL ? 'حدث خطأ أثناء الحفظ' : 'Error saving user'));
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async (user: User) => {
    try {
      await axiosInstance.post(`/Users/${user.userId}/unlock`);
      loadUsers();
    } catch (err: any) {
      console.error(err);
    }
  };

  const loadSessions = async () => {
    setSessionsLoading(true);
    try {
      const { data } = await axiosInstance.get('/Users/active-sessions');
      setSessions(data);
    } catch (err) { console.error(err); }
    finally { setSessionsLoading(false); }
  };

  const openSessionMonitor = () => { setSessionDialogOpen(true); loadSessions(); };

  const openLoginHistory = async (s: { userId: number; userName: string; fullName: string }) => {
    setHistoryUser(s);
    setHistoryDialogOpen(true);
    setHistory([]);
    setHistoryLoading(true);
    try {
      const { data } = await axiosInstance.get(`/Users/${s.userId}/login-history?take=5`);
      setHistory(data);
    } catch (err) { console.error(err); }
    finally { setHistoryLoading(false); }
  };

  const toggleMonitor = async (userId: number, current: boolean) => {
    setMonitorBusyId(userId);
    try {
      await axiosInstance.put(`/Users/${userId}/monitor`, { isLoginMonitored: !current });
      setSessions(prev => prev.map(s => s.userId === userId ? { ...s, isLoginMonitored: !current } : s));
    } catch (err) { console.error(err); }
    finally { setMonitorBusyId(null); }
  };

  const fmtDateTime = (iso?: string) =>
    iso ? new Date(iso).toLocaleString(isRTL ? 'ar-KW' : 'en-GB',
      { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' }) : '-';

  const fmtDuration = (login?: string, logout?: string) => {
    if (!login || !logout) return '-';
    const ms = new Date(logout).getTime() - new Date(login).getTime();
    if (ms < 0) return '-';
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m}${isRTL ? ' د' : 'm'}`;
    const h = Math.floor(m / 60);
    return `${h}${isRTL ? ' س ' : 'h '}${m % 60}${isRTL ? ' د' : 'm'}`;
  };

  const parseUA = (ua?: string) => {
    if (!ua) return { device: '-', os: '-', browser: '-', icon: '❓', full: '-' };
    let device = isRTL ? 'حاسوب' : 'Desktop';
    let icon = '💻';
    if (/iPad/.test(ua)) { device = 'iPad'; icon = '📱'; }
    else if (/iPhone|iPod/.test(ua)) { device = 'iPhone'; icon = '📱'; }
    else if (/Android/.test(ua) && /Mobile/.test(ua)) { device = isRTL ? 'هاتف أندرويد' : 'Android Phone'; icon = '📱'; }
    else if (/Android/.test(ua)) { device = isRTL ? 'تابلت أندرويد' : 'Android Tablet'; icon = '📱'; }
    else if (/Mobile/.test(ua)) { device = isRTL ? 'هاتف' : 'Mobile'; icon = '📱'; }
    else if (/Macintosh|Mac OS/.test(ua)) { device = 'Mac'; icon = '🖥️'; }
    else if (/Windows/.test(ua)) { device = 'Windows PC'; icon = '🖥️'; }
    else if (/Linux/.test(ua)) { device = 'Linux PC'; icon = '🖥️'; }

    let os = '-';
    let m;
    if (/Windows NT 10\.0/.test(ua)) os = 'Windows 10/11';
    else if (/Windows NT 6\.3/.test(ua)) os = 'Windows 8.1';
    else if (/Windows NT 6\.2/.test(ua)) os = 'Windows 8';
    else if (/Windows NT 6\.1/.test(ua)) os = 'Windows 7';
    else if ((m = ua.match(/Mac OS X (\d+[._]\d+(?:[._]\d+)?)/))) os = 'macOS ' + m[1].replace(/_/g, '.');
    else if ((m = ua.match(/iPhone OS (\d+_\d+(?:_\d+)?)/))) os = 'iOS ' + m[1].replace(/_/g, '.');
    else if ((m = ua.match(/CPU OS (\d+_\d+(?:_\d+)?)/))) os = 'iOS ' + m[1].replace(/_/g, '.');
    else if ((m = ua.match(/Android (\d+(?:\.\d+)?)/))) os = 'Android ' + m[1];
    else if (/Linux/.test(ua)) os = 'Linux';

    let browser = '-';
    if (/Edg\/(\d+)/.test(ua)) browser = 'Edge ' + (ua.match(/Edg\/(\d+)/)?.[1] || '');
    else if (/OPR\/(\d+)/.test(ua)) browser = 'Opera ' + (ua.match(/OPR\/(\d+)/)?.[1] || '');
    else if (/SamsungBrowser\/(\d+)/.test(ua)) browser = 'Samsung ' + (ua.match(/SamsungBrowser\/(\d+)/)?.[1] || '');
    else if (/FxiOS\/(\d+)/.test(ua)) browser = 'Firefox iOS ' + (ua.match(/FxiOS\/(\d+)/)?.[1] || '');
    else if (/CriOS\/(\d+)/.test(ua)) browser = 'Chrome iOS ' + (ua.match(/CriOS\/(\d+)/)?.[1] || '');
    else if (/Chrome\/(\d+)/.test(ua) && !/Edg|OPR/.test(ua)) browser = 'Chrome ' + (ua.match(/Chrome\/(\d+)/)?.[1] || '');
    else if (/Firefox\/(\d+)/.test(ua)) browser = 'Firefox ' + (ua.match(/Firefox\/(\d+)/)?.[1] || '');
    else if (/Version\/(\d+).*Safari/.test(ua)) browser = 'Safari ' + (ua.match(/Version\/(\d+)/)?.[1] || '');
    else if (/Safari/.test(ua)) browser = 'Safari';

    return { device, os, browser, icon, full: ua };
  };

  const stats = {
    total: users.length,
    active: users.filter(u => u.sessionStatus === 'Active').length,
    admins: users.filter(u => u.userRoles?.some(ur => ur.role.roleName === 'Admin' || ur.role.roleName === 'Super Admin')).length,
    blocked: users.filter(u => u.isLocked).length
  };

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const getRoleColor = (roleName: string) => {
    if (roleName === 'Super Admin') return { bg: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a5a 100%)', color: '#fff' };
    if (roleName === 'Admin') return { bg: 'linear-gradient(135deg, #ffa726 0%, #ff9800 100%)', color: '#fff' };
    return { bg: isDark ? 'rgba(144, 202, 249, 0.2)' : 'rgba(102, 126, 234, 0.15)', color: isDark ? '#90caf9' : '#667eea' };
  };

  const getSessionColor = (status: string) => {
    if (status === 'Active') return '#43a047';
    if (status === 'Sleep') return '#fb8c00';
    return '#9e9e9e';
  };

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);

  const getAvatarColor = (index: number) => {
    const colors = ['linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
      'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'];
    return colors[index % colors.length];
  };

  return (
    <Box sx={{ p: 3 }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Fade in timeout={500}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', mb: 1 }}>{t.title}</Typography>
          <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
        </Box>
      </Fade>

      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {[
          { value: stats.total, label: t.totalUsers, icon: <PeopleIcon />, gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' },
          { value: stats.active, label: t.activeUsers, icon: <CheckCircleIcon />, gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' },
          { value: stats.admins, label: t.adminUsers, icon: <SupervisorIcon />, gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' },
          { value: stats.blocked, label: t.blockedUsers, icon: <BlockIcon />, gradient: 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)' },
        ].map((stat, index) => (
          <Zoom in timeout={300 + index * 100} key={stat.label}>
            <Card elevation={0} sx={{ flex: '1 1 200px', minWidth: 180, background: stat.gradient, color: 'white', borderRadius: 3, transition: 'all 0.3s ease', '&:hover': { transform: 'translateY(-8px)', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' } }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h3" fontWeight="bold" sx={{ textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>{stat.value}</Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>{stat.label}</Typography>
                  </Box>
                  <Box sx={{ opacity: 0.3, transform: 'scale(2)' }}>{stat.icon}</Box>
                </Box>
              </CardContent>
            </Card>
          </Zoom>
        ))}
      </Box>

      <Fade in timeout={700}>
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(102, 126, 234, 0.2)'}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <TextField placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} size="small" sx={{ minWidth: 220, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#667eea' }} /></InputAdornment> }} />

            <TextField select size="small" label={t.status} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} sx={{ minWidth: 130, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              {[{ v: '', l: isRTL ? 'الكل' : 'All' }, { v: 'active', l: t.active }, { v: 'inactive', l: t.inactive }, { v: 'locked', l: t.locked }].map(o => (
                <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>
              ))}
            </TextField>

            <Autocomplete size="small" options={roles} getOptionLabel={(r) => isRTL ? (r.roleNameAr || r.roleName) : r.roleName}
              value={roles.find(r => r.roleName === filterRole) || null}
              onChange={(_, v) => setFilterRole(v?.roleName || '')}
              renderInput={(params) => <TextField {...params} label={t.roles} />}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <Autocomplete size="small" options={branches} getOptionLabel={(b) => isRTL ? (b.branchNameAr || b.branchName) : b.branchName}
              value={branches.find(b => b.branchName === filterBranch) || null}
              onChange={(_, v) => setFilterBranch(v?.branchName || '')}
              renderInput={(params) => <TextField {...params} label={isRTL ? 'الفرع' : 'Branch'} />}
              sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />

            <TextField select size="small" label={isRTL ? 'الاتصال' : 'Session'} value={filterSession} onChange={(e) => setFilterSession(e.target.value)} sx={{ minWidth: 120, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              {[{ v: '', l: isRTL ? 'الكل' : 'All' }, { v: 'online', l: t.online }, { v: 'offline', l: t.offline }].map(o => (
                <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>
              ))}
            </TextField>
            <Stack direction="row" spacing={1.5}>
              <Tooltip title={t.refresh} arrow>
                <IconButton onClick={loadUsers} sx={{ bgcolor: isDark ? 'rgba(102, 126, 234, 0.2)' : 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.3)', transform: 'rotate(180deg)' }, transition: 'all 0.3s ease' }}>
                  <RefreshIcon sx={{ color: '#667eea' }} />
                </IconButton>
              </Tooltip>
              {isSuperAdmin && (
                <Button variant="outlined" startIcon={<SessionIcon />} onClick={openSessionMonitor}
                  sx={{ borderRadius: 2, px: 2, borderColor: '#11998e', color: '#11998e', '&:hover': { bgcolor: 'rgba(17,153,142,0.08)' } }}>
                  {t.sessionMonitor}
                </Button>
              )}
              <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleAdd} sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', borderRadius: 2, px: 3, boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)', '&:hover': { boxShadow: '0 6px 20px rgba(102, 126, 234, 0.6)', transform: 'translateY(-2px)' }, transition: 'all 0.3s ease' }}>{t.addUser}</Button>
            </Stack>
          </Box>
        </Paper>
      </Fade>

      <Fade in timeout={900}>
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', background: isDark ? 'rgba(255,255,255,0.05)' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}><CircularProgress sx={{ color: '#667eea' }} /></Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                  {[t.userName, t.fullName, t.email, t.roles, t.status, t.sessionStatus, t.lastLogin, t.actions].map(h => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold', fontSize: '0.95rem', py: 2 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 8 }}><PeopleIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} /><Typography color="text.secondary" variant="h6">{t.noData}</Typography></TableCell></TableRow>
                ) : paginatedUsers.map((user, index) => (
                  <TableRow key={user.userId} hover sx={{ '&:hover': { bgcolor: isDark ? 'rgba(102, 126, 234, 0.1)' : 'rgba(102, 126, 234, 0.05)' }, transition: 'background 0.2s ease' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ background: getAvatarColor(index), fontWeight: 'bold' }}>{getInitials(user.fullName || user.userName)}</Avatar>
                        <Box><Typography fontWeight="600" color="primary.main">{user.userName}</Typography><Typography variant="caption" color="text.secondary">{user.phone || '-'}</Typography></Box>
                      </Box>
                    </TableCell>
                    <TableCell><Typography fontWeight="500">{isRTL ? user.fullNameAr || user.fullName : user.fullName}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">{user.email || '-'}</Typography></TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {user.userRoles?.map(ur => {
                          const roleStyle = getRoleColor(ur.role.roleName);
                          return <Chip key={ur.role.roleId} label={isRTL ? ur.role.roleNameAr : ur.role.roleName} size="small" sx={{ background: roleStyle.bg, color: roleStyle.color, fontWeight: 600, fontSize: '0.75rem' }} />;
                        })}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip icon={user.isActive ? <VerifiedIcon sx={{ fontSize: 16 }} /> : <BlockIcon sx={{ fontSize: 16 }} />} label={user.isActive ? t.active : t.inactive} size="small"
                          sx={{ background: user.isActive ? 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)' : 'linear-gradient(135deg, #eb3349 0%, #f45c43 100%)', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { color: '#fff' } }} />
                        {user.isLocked && (
                          <Tooltip title={t.locked}><LockIcon sx={{ fontSize: 16, color: '#e53935' }} /></Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={`${user.lastLoginIp || ''}${user.lastLoginCountry ? ' — ' + user.lastLoginCountry : ''}`}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <CircleIcon sx={{ fontSize: 10, color: getSessionColor(user.sessionStatus) }} />
                          <Typography variant="caption" fontWeight={600} sx={{ color: getSessionColor(user.sessionStatus) }}>
                            {user.sessionStatus === 'Active' ? t.online : user.sessionStatus === 'Sleep' ? t.idle : t.offline}
                          </Typography>
                        </Stack>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {user.lastLoginDate ? new Date(user.lastLoginDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' }) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={t.edit} arrow><IconButton size="small" onClick={() => handleEdit(user)} sx={{ color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.1)', transform: 'scale(1.1)' } }}><EditIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title={t.permissions} arrow><IconButton size="small" onClick={() => window.location.href = `/settings/user-permissions/${user.userId}`} sx={{ color: '#f093fb', '&:hover': { bgcolor: 'rgba(240, 147, 251, 0.1)', transform: 'scale(1.1)' } }}><SecurityIcon fontSize="small" /></IconButton></Tooltip>
                        <Tooltip title={t.fiscalYears} arrow><IconButton size="small" onClick={() => window.location.href = `/settings/user-fiscal-years/${user.userId}`} sx={{ color: '#4facfe', '&:hover': { bgcolor: 'rgba(79, 172, 254, 0.1)', transform: 'scale(1.1)' } }}><CalendarIcon fontSize="small" /></IconButton></Tooltip>
                        {isSuperAdmin && user.isLocked && (
                          <Tooltip title={t.unlock} arrow><IconButton size="small" onClick={() => handleUnlock(user)} sx={{ color: '#43a047', '&:hover': { bgcolor: 'rgba(67,160,71,0.1)', transform: 'scale(1.1)' } }}><UnlockIcon fontSize="small" /></IconButton></Tooltip>
                        )}
                        <Tooltip title={t.delete} arrow><IconButton size="small" onClick={() => handleDelete(user.userId)} sx={{ color: '#f45c43', '&:hover': { bgcolor: 'rgba(244, 92, 67, 0.1)', transform: 'scale(1.1)' } }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <TablePagination rowsPerPageOptions={[5, 10, 20, 50]} component="div" count={filteredUsers.length} rowsPerPage={rowsPerPage} page={page} onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }} labelRowsPerPage={t.rowsPerPage} sx={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }} />
        </TableContainer>
      </Fade>

      {/* ── Session Monitor Dialog ── */}
      <Dialog open={sessionDialogOpen} onClose={() => setSessionDialogOpen(false)} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <SessionIcon />
            <Typography fontWeight="bold">{t.sessionMonitor}</Typography>
          </Box>
          <Box display="flex" gap={1}>
            <IconButton size="small" onClick={loadSessions} sx={{ color: '#fff' }}><RefreshIcon /></IconButton>
            <IconButton size="small" onClick={() => setSessionDialogOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {sessionsLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress sx={{ color: '#11998e' }} /></Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f5f5f5' }}>
                  {[t.userName, t.sessionStatus, t.lastLogin, t.lastActivity, t.lastLogout, t.ipAddress, t.country, t.status, t.actions2].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: '0.8rem' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((s: any) => (
                  <TableRow key={s.userId} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircleIcon sx={{ fontSize: 10, color: getSessionColor(s.sessionStatus) }} />
                        <Typography variant="body2" fontWeight={600}>{s.userName}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.fullName}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip label={s.sessionStatus === 'Active' ? t.online : s.sessionStatus === 'Sleep' ? t.idle : t.offline}
                        size="small" sx={{ bgcolor: getSessionColor(s.sessionStatus), color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell><Typography variant="caption">{s.lastLoginDate ? new Date(s.lastLoginDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' }) : '-'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{s.lastActivityDate ? new Date(s.lastActivityDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' }) : '-'}</Typography></TableCell>
                    <TableCell><Typography variant="caption">{s.lastLogoutDate ? new Date(s.lastLogoutDate).toLocaleString(isRTL ? 'ar-KW' : 'en-GB', { dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kuwait' }) : '-'}</Typography></TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <WifiIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="caption">{s.lastLoginIp || '-'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <GlobeIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        <Typography variant="caption">{s.lastLoginCountry || '-'}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {s.isLocked ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Chip label={t.locked} size="small" sx={{ bgcolor: '#e53935', color: '#fff', fontSize: '0.7rem' }} />
                          <Tooltip title={t.unlock}>
                            <IconButton size="small" onClick={() => handleUnlock({ userId: s.userId } as User)} sx={{ color: '#43a047' }}>
                              <UnlockIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ) : (
                        <Chip label={s.isActive !== false ? t.active : t.inactive} size="small"
                          sx={{ bgcolor: s.isActive !== false ? '#43a047' : '#9e9e9e', color: '#fff', fontSize: '0.7rem' }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Tooltip title={t.last5Logins} arrow>
                          <IconButton size="small" onClick={() => openLoginHistory(s)} sx={{ color: '#1976d2' }}>
                            <HistoryIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={s.isLoginMonitored ? `${t.monitorLogin}: ${t.monitoringOn}` : `${t.monitorLogin}: ${t.monitoringOff} — ${t.monitorTooltip}`} arrow>
                          <span>
                            <IconButton size="small"
                              disabled={monitorBusyId === s.userId}
                              onClick={() => toggleMonitor(s.userId, !!s.isLoginMonitored)}
                              sx={{ color: s.isLoginMonitored ? '#e53935' : '#9e9e9e' }}>
                              {s.isLoginMonitored
                                ? <MonitorOnIcon sx={{ fontSize: 18 }} />
                                : <MonitorOffIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Login History Dialog ── */}
      <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth dir={isRTL ? 'rtl' : 'ltr'}
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <HistoryIcon />
            <Box>
              <Typography fontWeight="bold">{t.last5Logins}</Typography>
              {historyUser && (
                <Typography variant="caption" sx={{ opacity: 0.85 }}>
                  {historyUser.userName} — {historyUser.fullName}
                </Typography>
              )}
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setHistoryDialogOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : '#fafafa' }}>
          {historyLoading ? (
            <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
          ) : history.length === 0 ? (
            <Box display="flex" justifyContent="center" py={6}>
              <Typography variant="body2" color="text.secondary">{t.noHistory}</Typography>
            </Box>
          ) : (
            <Stack spacing={1.5}>
              {history.map((h: any, idx: number) => {
                const ua = parseUA(h.userAgent);
                return (
                  <Paper key={h.loginHistoryId} elevation={0} sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
                    bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fff'
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, pb: 1, borderBottom: `1px dashed ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }}>
                      <Chip label={`#${idx + 1}`} size="small" sx={{ bgcolor: '#1976d2', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />
                      <Typography variant="body2" fontWeight={700} sx={{ color: isDark ? '#90caf9' : '#1976d2' }}>
                        {fmtDateTime(h.loginDate)}
                      </Typography>
                      {h.logoutDate && (
                        <>
                          <Typography variant="caption" color="text.secondary">←→</Typography>
                          <Typography variant="caption" color="text.secondary">{fmtDateTime(h.logoutDate)}</Typography>
                          <Chip label={fmtDuration(h.loginDate, h.logoutDate)} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#43a047', color: '#fff' }} />
                        </>
                      )}
                      {!h.logoutDate && (
                        <Chip label={isRTL ? 'الجلسة مازالت مفتوحة' : 'Session still open'} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#fb8c00', color: '#fff' }} />
                      )}
                    </Box>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 2fr' }, gap: 2 }}>
                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                          {t.ipAddress}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <WifiIcon sx={{ fontSize: 14, color: '#1976d2' }} />
                          <Typography variant="body2" fontFamily="monospace" fontWeight={600}>
                            {h.ipAddress || '-'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                          {t.country}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <GlobeIcon sx={{ fontSize: 14, color: '#43a047' }} />
                          <Typography variant="body2" fontWeight={600}>
                            {h.country || '-'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                          {t.userAgent}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                          <Typography variant="body2" fontWeight={700}>
                            {ua.icon} {ua.device}
                          </Typography>
                          {ua.os !== '-' && (
                            <Chip label={ua.os} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: isDark ? 'rgba(144,202,249,0.2)' : 'rgba(25,118,210,0.1)', color: isDark ? '#90caf9' : '#1976d2' }} />
                          )}
                          {ua.browser !== '-' && (
                            <Chip label={ua.browser} size="small" sx={{ height: 20, fontSize: '0.65rem', bgcolor: isDark ? 'rgba(67,160,71,0.2)' : 'rgba(67,160,71,0.1)', color: isDark ? '#81c784' : '#2e7d32' }} />
                          )}
                        </Box>
                        {ua.full !== '-' && (
                          <Tooltip title={isRTL ? 'النص الكامل لـ User-Agent' : 'Full User-Agent string'} arrow placement="top">
                            <Typography variant="caption" color="text.secondary" sx={{
                              display: 'block', mt: 0.5, fontSize: '0.65rem', fontFamily: 'monospace',
                              wordBreak: 'break-all', lineHeight: 1.3,
                              maxHeight: 36, overflow: 'auto'
                            }}>
                              {ua.full}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth 
        dir={isRTL ? 'rtl' : 'ltr'} 
        TransitionComponent={Fade}
        transitionDuration={300}
        PaperProps={{ 
          sx: { 
            borderRadius: 4, 
            bgcolor: isDark ? '#121212' : '#ffffff',
            overflow: 'hidden',
            boxShadow: isDark 
              ? '0 24px 48px rgba(0, 0, 0, 0.5)' 
              : '0 24px 48px rgba(102, 126, 234, 0.25)'
          } 
        }}
      >
        <DialogTitle sx={{ 
          background: isDark 
            ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' 
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
          color: 'white', 
          p: 0,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Box sx={{ position: 'absolute', top: -30, right: -30, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)' }} />
          <Box sx={{ position: 'absolute', bottom: -20, left: '40%', width: 60, height: 60, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2.5, position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                width: 48, 
                height: 48, 
                borderRadius: 2, 
                bgcolor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}>
                {selectedUser ? <EditIcon sx={{ fontSize: 26 }} /> : <PersonAddIcon sx={{ fontSize: 26 }} />}
              </Box>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {selectedUser ? t.editUser : t.createUser}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  {selectedUser ? `@${selectedUser.userName}` : t.userDetails}
                </Typography>
              </Box>
            </Box>
            <IconButton 
              onClick={() => setDialogOpen(false)} 
              sx={{ 
                color: 'white', 
                bgcolor: 'rgba(255,255,255,0.15)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.25)', transform: 'rotate(90deg)' },
                transition: 'all 0.3s ease'
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: isDark ? '#1a1a1a' : '#fafafa' }}>
          {error && (
            <Fade in>
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            </Fade>
          )}

          {/* Username Section */}
          <Paper elevation={0} sx={{ 
            p: 2.5, 
            mb: 2.5, 
            borderRadius: 3, 
            bgcolor: isDark ? 'rgba(144, 202, 249, 0.08)' : 'rgba(102, 126, 234, 0.05)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <SecurityIcon sx={{ color: isDark ? '#90caf9' : '#667eea', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#90caf9' : '#667eea' }}>
                {isRTL ? 'بيانات الحساب' : 'Account Info'}
              </Typography>
            </Box>
            <TextField 
              label={t.userName} 
              fullWidth 
              value={formData.userName} 
              onChange={e => setFormData({ ...formData, userName: e.target.value })} 
              disabled={!!selectedUser}
              placeholder={isRTL ? 'أدخل اسم المستخدم' : 'Enter username'}
              InputProps={{
                startAdornment: <InputAdornment position="start"><PeopleIcon sx={{ color: 'text.secondary' }} /></InputAdornment>
              }}
              sx={{ 
                '& .MuiOutlinedInput-root': { 
                  borderRadius: 2,
                  bgcolor: isDark ? '#2d2d2d' : '#ffffff'
                } 
              }} 
            />
          </Paper>

          {/* Personal Info Section */}
          <Paper elevation={0} sx={{ 
            p: 2.5, 
            mb: 2.5, 
            borderRadius: 3, 
            bgcolor: isDark ? 'rgba(206, 147, 216, 0.08)' : 'rgba(118, 75, 162, 0.05)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <PeopleIcon sx={{ color: isDark ? '#ce93d8' : '#764ba2', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#ce93d8' : '#764ba2' }}>
                {isRTL ? 'المعلومات الشخصية' : 'Personal Info'}
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  label={t.fullName} 
                  fullWidth 
                  value={formData.fullName} 
                  onChange={e => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder={isRTL ? 'الاسم بالإنجليزية' : 'Full name in English'}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }} 
                />
                <TextField 
                  label={t.fullNameAr} 
                  fullWidth 
                  value={formData.fullNameAr} 
                  onChange={e => setFormData({ ...formData, fullNameAr: e.target.value })}
                  placeholder={isRTL ? 'الاسم بالعربية' : 'Full name in Arabic'}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }} 
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  label={t.email} 
                  fullWidth 
                  type="email"
                  value={formData.email} 
                  onChange={e => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@example.com"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }} 
                />
                <TextField 
                  label={t.phone} 
                  fullWidth 
                  value={formData.phone} 
                  onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+965 xxxx xxxx"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }} 
                />
              </Box>
            </Stack>
          </Paper>

          {/* Password Section - Only for new users */}
          {!selectedUser && (
            <Paper elevation={0} sx={{ 
              p: 2.5, 
              mb: 2.5, 
              borderRadius: 3, 
              bgcolor: isDark ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.05)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon sx={{ color: isDark ? '#ef5350' : '#f44336', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#ef5350' : '#f44336' }}>
                  {isRTL ? 'كلمة المرور' : 'Password'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField 
                  label={t.password} 
                  type="password" 
                  fullWidth 
                  value={formData.password} 
                  onChange={e => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }} 
                />
                <TextField 
                  label={t.confirmPassword} 
                  type="password" 
                  fullWidth 
                  value={formData.confirmPassword} 
                  onChange={e => setFormData({ ...formData, confirmPassword: e.target.value })}
                  placeholder="••••••••"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }} 
                />
              </Box>
            </Paper>
          )}

          {/* Reset Password Section - Only for editing existing users */}
          {selectedUser && (
            <Paper elevation={0} sx={{ 
              p: 2.5, 
              mb: 2.5, 
              borderRadius: 3, 
              bgcolor: isDark ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 152, 0, 0.05)',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SecurityIcon sx={{ color: isDark ? '#ffb74d' : '#ff9800', fontSize: 20 }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#ffb74d' : '#ff9800' }}>
                  {t.resetPassword}
                </Typography>
              </Box>
              <TextField 
                label={t.newPassword}
                type="password" 
                fullWidth 
                value={formData.newPassword} 
                onChange={e => setFormData({ ...formData, newPassword: e.target.value })}
                placeholder="••••••••"
                helperText={t.leaveEmptyToKeep}
                sx={{ 
                  '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' },
                  '& .MuiFormHelperText-root': { color: isDark ? '#b0b0b0' : '#666666' }
                }} 
              />
            </Paper>
          )}

          {/* Branch Access Section */}
          <Paper elevation={0} sx={{ 
            p: 2.5, 
            mb: 2.5, 
            borderRadius: 3, 
            bgcolor: isDark ? 'rgba(79, 195, 247, 0.08)' : 'rgba(3, 169, 244, 0.05)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <StoreIcon sx={{ color: isDark ? '#4fc3f7' : '#03a9f4', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#4fc3f7' : '#03a9f4' }}>
                {t.branchAccess}
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Autocomplete
                options={branches}
                getOptionLabel={(b) => isRTL ? b.branchNameAr : b.branchName}
                value={branches.find(b => b.branchId === formData.branchId) || null}
                onChange={(_, v) => setFormData({ ...formData, branchId: v?.branchId || null })}
                isOptionEqualToValue={(o, v) => o.branchId === v.branchId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t.mainBranch}
                    placeholder={t.selectBranch}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }}
                  />
                )}
              />

              <Autocomplete
                multiple
                limitTags={2}
                options={branches}
                getOptionLabel={(b) => isRTL ? b.branchNameAr : b.branchName}
                value={branches.filter(b => formData.allowedBranchIds.includes(b.branchId))}
                onChange={(_, v) => setFormData({ ...formData, allowedBranchIds: v.map(b => b.branchId) })}
                isOptionEqualToValue={(o, v) => o.branchId === v.branchId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t.allowedBranches}
                    placeholder={t.selectAllowedBranches}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((b, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={b.branchId}
                      label={isRTL ? b.branchNameAr : b.branchName}
                      size="small"
                      sx={{ bgcolor: isDark ? 'rgba(79, 195, 247, 0.2)' : 'rgba(3, 169, 244, 0.15)', color: isDark ? '#4fc3f7' : '#03a9f4' }}
                    />
                  ))
                }
              />
            </Stack>
          </Paper>

          {/* ✅ Linked Employee Section */}
          <Paper elevation={0} sx={{ 
            p: 2.5, 
            mb: 2.5, 
            borderRadius: 3, 
            bgcolor: isDark ? 'rgba(255, 167, 38, 0.08)' : 'rgba(255, 152, 0, 0.05)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <BadgeIcon sx={{ color: isDark ? '#ffa726' : '#ff9800', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#ffa726' : '#ff9800' }}>
                {t.linkedEmployee}
              </Typography>
            </Box>
            <Autocomplete
              options={employees}
              getOptionLabel={(emp) => `${isRTL ? (emp.fullNameAr || emp.fullName) : emp.fullName} ${emp.employeeCode ? '(' + emp.employeeCode + ')' : ''}`}
              value={employees.find(e => e.employeeId === formData.employeeId) || null}
              onChange={(_, v) => setFormData({ ...formData, employeeId: v?.employeeId || null })}
              isOptionEqualToValue={(option, value) => option.employeeId === value.employeeId}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={t.selectEmployee}
                  placeholder={t.noEmployee}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } }}
                />
              )}
            />
          </Paper>

          {/* Roles & Status Section */}
          <Paper elevation={0} sx={{ 
            p: 2.5, 
            borderRadius: 3, 
            bgcolor: isDark ? 'rgba(102, 187, 106, 0.08)' : 'rgba(76, 175, 80, 0.05)',
            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AdminIcon sx={{ color: isDark ? '#66bb6a' : '#4caf50', fontSize: 20 }} />
              <Typography variant="subtitle2" fontWeight="bold" sx={{ color: isDark ? '#66bb6a' : '#4caf50' }}>
                {isRTL ? 'الأدوار والحالة' : 'Roles & Status'}
              </Typography>
            </Box>
            <Stack spacing={2}>
              <Autocomplete
                multiple
                limitTags={2}
                options={roles}
                getOptionLabel={(r) => isRTL ? r.roleNameAr : r.roleName}
                value={roles.filter(r => formData.roleIds.includes(r.roleId))}
                onChange={(_, v) => setFormData({ ...formData, roleIds: v.map(r => r.roleId) })}
                isOptionEqualToValue={(o, v) => o.roleId === v.roleId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t.selectRoles}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: isDark ? '#2d2d2d' : '#ffffff',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: isDark ? '#66bb6a' : '#4caf50' }
                      }
                    }}
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((r, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={r.roleId}
                      label={isRTL ? r.roleNameAr : r.roleName}
                      size="small"
                      sx={{
                        background: isDark
                          ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        fontWeight: 500
                      }}
                    />
                  ))
                }
              />

              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                p: 2,
                borderRadius: 2,
                bgcolor: formData.isActive 
                  ? (isDark ? 'rgba(102, 187, 106, 0.15)' : 'rgba(76, 175, 80, 0.1)')
                  : (isDark ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.1)'),
                border: `1px solid ${formData.isActive 
                  ? (isDark ? 'rgba(102, 187, 106, 0.3)' : 'rgba(76, 175, 80, 0.3)')
                  : (isDark ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.3)')}`,
                transition: 'all 0.3s ease'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {formData.isActive 
                    ? <CheckCircleIcon sx={{ color: isDark ? '#66bb6a' : '#4caf50' }} />
                    : <BlockIcon sx={{ color: isDark ? '#ef5350' : '#f44336' }} />
                  }
                  <Typography fontWeight="600">
                    {formData.isActive ? t.active : t.inactive}
                  </Typography>
                </Box>
                <Switch 
                  checked={formData.isActive} 
                  onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                  sx={{ 
                    '& .MuiSwitch-switchBase.Mui-checked': { 
                      color: isDark ? '#66bb6a' : '#4caf50'
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { 
                      backgroundColor: isDark ? '#66bb6a' : '#4caf50'
                    }
                  }} 
                />
              </Box>
            </Stack>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ 
          p: 2.5, 
          gap: 1.5,
          bgcolor: isDark ? '#1e1e1e' : '#f5f5f5',
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`
        }}>
          <Button 
            onClick={() => setDialogOpen(false)}
            variant="outlined"
            startIcon={<CloseIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3,
              borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
              color: isDark ? '#b0b0b0' : '#666666',
              '&:hover': {
                borderColor: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
                bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
              }
            }}
          >
            {t.cancel}
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave} 
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            disabled={saving}
            sx={{ 
              background: isDark 
                ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)'
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              borderRadius: 2, 
              px: 4,
              boxShadow: isDark 
                ? '0 4px 15px rgba(30, 60, 114, 0.4)'
                : '0 4px 15px rgba(102, 126, 234, 0.4)',
              '&:hover': { 
                boxShadow: isDark 
                  ? '0 6px 20px rgba(30, 60, 114, 0.5)'
                  : '0 6px 20px rgba(102, 126, 234, 0.5)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            {saving ? (isRTL ? 'جاري الحفظ...' : 'Saving...') : t.save}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;